import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { ManifoldNextGenAccountMenu } from "~/components/ManifoldNextGenAccountMenu";
import { ManifoldNextGenNavPicker } from "~/components/ManifoldNextGenNavPicker";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import useNextGenHeader from "~/contexts/nextgen-header";
import SecondaryHeaderLogo from "~/customer/secondary-header-logo.svg";
import { cn } from "~/lib/utils";

interface ManifoldNextGenAppHeaderProps {
  isDark: boolean;
  onToggleDark: () => void;
  onSwitchToClassic: () => void;
}

export function ManifoldNextGenAppHeader({
  isDark,
  onToggleDark,
  onSwitchToClassic,
}: ManifoldNextGenAppHeaderProps) {
  const { breadcrumbs, title, actions, rightPane } = useNextGenHeader();
  const navigate = useNavigate();

  // nav-picker (auto) | breadcrumbs (1fr) | title (auto) | actions+avatar (auto) | optional rightPane
  const gridCols = rightPane
    ? "grid-cols-[auto_1fr_auto_auto_auto]"
    : "grid-cols-[auto_1fr_auto_auto]";

  return (
    <div className={cn("grid shrink-0 items-stretch border-b border-border bg-sidebar", gridCols)}>
      <div className="flex items-center pl-3">
        <ManifoldNextGenNavPicker />
      </div>

      <div className="flex items-center py-2 pl-2">
        {breadcrumbs.length > 0 ? (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <Fragment key={index}>
                    <BreadcrumbItem>
                      {isLast || !crumb.href ? (
                        <BreadcrumbPage className="text-sm font-medium text-foreground">
                          {crumb.label}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink
                          href={crumb.href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(crumb.href!);
                          }}
                          className="text-sm font-medium text-foreground opacity-70 transition-colors duration-200 hover:bg-black/[0.06] hover:opacity-100 dark:hover:bg-white/[0.08]"
                        >
                          {crumb.label}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        ) : (
          title && <span className="px-2 text-sm font-medium text-foreground">{title}</span>
        )}
      </div>

      <div className="flex items-center justify-center px-4 py-2">
        {breadcrumbs.length > 0 && title && (
          <span className="text-sm font-medium text-foreground">{title}</span>
        )}
      </div>

      <div className="flex items-center gap-2 py-2 pr-4">
        {actions[0]}
        {actions[1]}
        <img src={SecondaryHeaderLogo} alt="Customer Logo" className="h-6" />
        <ManifoldNextGenAccountMenu
          isDark={isDark}
          onToggleDark={onToggleDark}
          onSwitchToClassic={onSwitchToClassic}
        />
      </div>
      {rightPane}
    </div>
  );
}
